import { useCallback, useEffect, useRef, useState } from 'react';
import _debounce from 'lodash/debounce';

const useReachedToBottom = (
  elementRef: React.RefObject<HTMLElement>,
  handleReachedToBottom?: () => unknown,
  initialValue?: boolean
) => {
  const [isReachedToBottom, setIsReachedToBottom] = useState(
    initialValue || false
  );
  const isReachedToBottomRef = useRef(initialValue || false);

  const checkScrollPosition = useCallback(() => {
    if (!elementRef?.current || isReachedToBottomRef.current) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = elementRef.current;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight;

    if (!isAtBottom) return;
    setIsReachedToBottom(true);
    isReachedToBottomRef.current = true;
    handleReachedToBottom?.();
  }, [isReachedToBottom]);

  const debouncedCheckScrollPosition = _debounce(checkScrollPosition, 200);

  useEffect(() => {
    // Check scroll position initially in case the div is already scrolled to the bottom
    debouncedCheckScrollPosition();

    if (elementRef?.current) {
      elementRef.current.addEventListener(
        'scroll',
        debouncedCheckScrollPosition
      );
    }

    return () => {
      if (elementRef?.current) {
        elementRef.current.removeEventListener(
          'scroll',
          debouncedCheckScrollPosition
        );
      }
    };
  }, []);

  return isReachedToBottom;
};

export default useReachedToBottom;
